import classNames from "classnames";
import { maybe } from "shared/maybe";
import { ACE_EDITOR_HEIGHT } from "../config.js";
import { Mop } from "../namespaces/Mop.js";
export const MODAL_COL_SIZINGS = {
    xs: 3,
    sm: 3,
    md: 3,
    lg: 3,
    xl: 3,
    xxl: 3
};
export const DEFAULT_COL_SIZINGS = {
    xs: 0,
    sm: 4,
    md: 3,
    lg: 3,
    xl: 2,
    xxl: 2,
};
export const DEFAULT_ACE_EDITOR_PROPS = {
    height: ACE_EDITOR_HEIGHT,
    showPrintMargin: false,
    setOptions: {
        fontSize: "inherit"
    }
};
export function aceEditorProps(field) {
    return {
        onFocus: field.focus,
        onBlur: field.blur,
        onChange: field.setValue,
        readOnly: field.disabled,
        value: field.value ?? "",
        className: classNames("ace_editor", { "is-invalid": field.hasErrors, "disabled": field.disabled }),
    };
}
function setValueFromReactSelect(field, meta, value) {
    if (meta.action === "select-option" || meta.action === "deselect-option") {
        field.setValue(value);
    }
    else {
        field.setValueAndCommit(value);
    }
}
export function reactSelectProps(field, options = {}) {
    return {
        value: field.value,
        onFocus: field.focus,
        onBlur: field.blur,
        onMenuClose: field.blur,
        onChange: (newValue, meta) => setValueFromReactSelect(field, meta, newValue ?? undefined),
        isDisabled: field.disabled || (options.isDisabled ?? false),
        classNamePrefix: "react-select",
        className: "react-select" + (field.hasErrors ? " is-invalid" : ""),
    };
}
export function reactSelectMultiProps(field, options = {}) {
    return {
        isMulti: true,
        value: field.value,
        onFocus: field.focus,
        onBlur: field.blur,
        onMenuClose: field.blur,
        onChange: (newValue, meta) => setValueFromReactSelect(field, meta, newValue.length > 0 ? newValue : undefined),
        isDisabled: field.disabled || (options.isDisabled ?? false),
        classNamePrefix: "react-select",
        className: "react-select" + (field.hasErrors ? " is-invalid" : ""),
    };
}
export function reactSelectValueProps(field, options = {}) {
    return {
        value: maybe(field.value, value => ({ value, label: value }), () => null),
        onFocus: field.focus,
        onBlur: field.blur,
        onMenuClose: field.commit,
        onChange: (newValue, meta) => setValueFromReactSelect(field, meta, newValue?.value ?? undefined),
        isDisabled: field.disabled || (options.isDisabled ?? false),
        classNamePrefix: "react-select",
        className: "react-select" + (field.hasErrors ? " is-invalid" : ""),
    };
}
export function reactSelectMultiValueProps(field, options = {}) {
    return {
        isMulti: true,
        value: maybe(field.value, values => values.map(value => ({ value, label: value })), () => null),
        onFocus: field.focus,
        onBlur: field.blur,
        onMenuClose: field.commit,
        onChange: (newValue, meta) => setValueFromReactSelect(field, meta, newValue.length > 0 ? newValue.map(value => value.value) : undefined),
        isDisabled: field.disabled || (options.isDisabled ?? false),
        classNamePrefix: "react-select",
        className: "react-select" + (field.hasErrors ? " is-invalid" : ""),
    };
}
export function bsFileProps(field) {
    return {
        onChange: (event) => {
            if ("files" in event.currentTarget) {
                field.setValueAndCommit(event.currentTarget.files ?? undefined);
            }
        },
        disabled: field.disabled ?? false,
        onFocus: field.focus,
        onBlur: field.blur,
        isInvalid: field.hasErrors,
    };
}
export function bsStringProps(field) {
    return {
        value: field.value ?? "",
        onChange: (event) => field.setValue(event.currentTarget.value !== "" ? event.currentTarget.value : undefined),
        onFocus: field.focus,
        onBlur: field.blur,
        disabled: field.disabled ?? false,
        isInvalid: field.hasErrors,
    };
}
export function bsNumberProps(field) {
    return {
        type: "number",
        value: field.value ?? "",
        onChange: (event) => {
            if (event.currentTarget.value.trim() !== "") {
                const parsed = parseFloat(event.currentTarget.value);
                if (!isNaN(parsed)) {
                    field.setValue(parsed);
                }
            }
        },
        onFocus: field.focus,
        onBlur: field.blur,
        disabled: field.disabled ?? false,
        isInvalid: field.hasErrors,
    };
}
export function bsMultiCheckProps(field, currentValue, comparer = (a, b) => a === b) {
    return {
        checked: (field.value?.findIndex(_ => comparer(_, currentValue)) ?? -1) !== -1,
        onChange: (event) => field.setValueAndCommit(event.target.checked ? [...field.value ?? [], currentValue] : (field.value ?? []).filter(_ => !comparer(_, currentValue))),
        onFocus: field.focus,
        onBlur: field.blur,
        disabled: field.disabled ?? false,
        isInvalid: field.hasErrors,
    };
}
export function bsCheckProps(field, trueValue, falseValue, comparer = (a, b) => a === b) {
    return {
        checked: comparer(field.value, trueValue),
        onChange: (event) => field.setValueAndCommit(event.target.checked ? trueValue : falseValue),
        onFocus: field.focus,
        onBlur: field.blur,
        disabled: field.disabled ?? false,
        isInvalid: field.hasErrors,
    };
}
export function bsRadioProps(field, value, comparer = (a, b) => a === b) {
    return {
        checked: comparer(field.value, value),
        onChange: (event) => {
            if (event.target.checked) {
                field.setValueAndCommit(value);
            }
        },
        onFocus: field.focus,
        onBlur: field.blur,
        disabled: field.disabled ?? false,
        isInvalid: field.hasErrors,
    };
}
/**
 * Validate a form using the Mop library.
 */
export function formValidateWithMop(schema) {
    return async (value) => {
        const errors = Mop.execOrErrorsOnly(value, await schema(value));
        if (errors === undefined) {
            return;
        }
        return errors.map(error => {
            return {
                ...error,
                path: error.path ?? []
            };
        });
    };
}
